import { isBoolean, isString } from 'lodash';
import { UseQueryResult, useQuery } from 'react-query';
import { useUserClientsWithActiveClient } from 'domains/auth/UserClientsWrapper/useUserClients';
import {
  ClaimsResponse,
  getBillingInformationByClientId,
  getClaimById,
  getClaims,
  getPaymentById,
  getPaymentMethods,
  getPayments,
  getStripeAccountIdByState,
  getStripeRefundData,
  updatePaymentMethod,
} from 'domains/billing';

import { Claim, RawClaim } from 'domains/billing/types';
import { formatRawClaim } from 'domains/claims/data';
import { useMutation } from 'domains/data';

export const useBillingInfo = () => {
  const { contactId, activeClientId } = useUserClientsWithActiveClient();
  return useQuery(
    ['billing-information', activeClientId],
    () => getBillingInformationByClientId(contactId, activeClientId),
    {
      retry: false,
    },
  );
};

export const useClaim = (claimId?: string, automaticCoInsuranceCollection?: boolean): UseQueryResult<Claim> => {
  const { contactId, activeClientId } = useUserClientsWithActiveClient();

  const isClaimQueryEnabled = isBoolean(automaticCoInsuranceCollection) && isString(claimId);

  return useQuery(
    ['claim', claimId, activeClientId],
    () => getClaimById(contactId, activeClientId, claimId as string),
    {
      enabled: isClaimQueryEnabled,
      select: rawClaim => formatRawClaim(rawClaim),
      retry: false,
    },
  );
};

export const useClaims = (
  automaticCoInsuranceCollection?: boolean,
  page?: number,
): UseQueryResult<ClaimsResponse, Error> => {
  const { contactId, activeClientId } = useUserClientsWithActiveClient();

  const isClaimQueryEnabled = isBoolean(automaticCoInsuranceCollection);

  return useQuery(['claims', activeClientId, page], () => getClaims(contactId, activeClientId, page), {
    enabled: isClaimQueryEnabled,
    select: rawClaims => {
      return {
        ...rawClaims,
        items: rawClaims.items.map((rawClaim: RawClaim) => formatRawClaim(rawClaim)),
      };
    },

    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const usePayments = (page: number, nextToken: string) => {
  const { contactId, activeClientId } = useUserClientsWithActiveClient();
  return useQuery(['payments', activeClientId, page], () => getPayments(contactId, activeClientId, page, nextToken), {
    retry: false,
    refetchOnWindowFocus: false,
    select: payments => {
      return {
        ...payments,
        items: payments?.items.filter(receipt => receipt.hosted_invoice_url),
      };
    },
  });
};

export const usePayment = (paymentId: string, options?: { enabled?: boolean }) => {
  const { contactId, activeClientId } = useUserClientsWithActiveClient();
  return useQuery(['payments', activeClientId], () => getPaymentById(contactId, activeClientId, paymentId), {
    retry: false,
    enabled: options?.enabled ?? true,
  });
};

export const useUpdatePaymentMethod = () => {
  const { contactId, activeClientId } = useUserClientsWithActiveClient();
  return useMutation(
    (stripePaymentMethodID: string) => updatePaymentMethod(contactId, activeClientId, stripePaymentMethodID),
    {
      invalidateQueries: [['payment-methods', activeClientId]],
    },
  );
};

/** @NOTE(juan): response is always an array but we only care about the first method (if exists) */
/** @todo(juan): manually recreated options object type for now but we should find a way to extract it from the library instead of re-defining it */
export const useFirstPaymentMethod = (options?: {
  enabled?: boolean;
  useErrorBoundary?: boolean;
  onError?: (error: unknown) => void;
}) => {
  const { enabled, onError, useErrorBoundary } = options || {};
  const { contactId, activeClientId } = useUserClientsWithActiveClient();
  return useQuery(['payment-methods', activeClientId], () => getPaymentMethods(contactId, activeClientId), {
    retry: false,
    select: methods => methods[0],
    enabled,
    useErrorBoundary,
    onError,
  });
};

export const useStripeRefundData = (chargeId: string) => {
  const { contactId, activeClientId } = useUserClientsWithActiveClient();
  return useQuery(
    ['stripe-refund-data', activeClientId],
    () => getStripeRefundData(contactId, activeClientId, chargeId),
    {
      enabled: !!chargeId,
    },
  );
};

export const useStripeAccountId = (state: string) => {
  return useQuery(['stripe-account-id', state], () => getStripeAccountIdByState(state));
};
